import React, { useState, useEffect, useMemo } from 'react';
import { ShieldCheck, Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import FilterBar from './FilterBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GateMaster() {
  const [allStaff, setAllStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newGate, setNewGate] = useState({ name: '', mobile: '', staff_id: '' });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/staff`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await r.json();
      setAllStaff((data || []).filter(s => s.role === 'gate' && s.status !== 'deleted'));
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allStaff.filter(s => {
      if (statusFilter !== 'all' && (s.status || 'active') !== statusFilter) return false;
      if (q && !(
        (s.name || '').toLowerCase().includes(q) ||
        (s.mobile || '').toLowerCase().includes(q) ||
        (s.staff_id || '').toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [allStaff, search, statusFilter]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(
        `${API_URL}/api/admin/gate-staff?name=${encodeURIComponent(newGate.name)}&mobile=${encodeURIComponent(newGate.mobile)}&staff_id=${encodeURIComponent(newGate.staff_id)}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await r.json();
      if (r.ok) {
        alert(data.message);
        setShowAdd(false);
        setNewGate({ name: '', mobile: '', staff_id: '' });
        fetchStaff();
      } else {
        alert(data.detail || 'Failed to create gate user');
      }
    } catch (err) {
      alert('Error creating gate user');
    }
  };

  const startEdit = (s) => {
    setEditing(s.id);
    setEditForm({ name: s.name || '', staff_id: s.staff_id || '', status: s.status || 'active' });
  };

  const saveEdit = async (id) => {
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/admin/staff/${id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(editForm)
    });
    if (r.ok) {
      setEditing(null);
      fetchStaff();
    } else {
      const d = await r.json();
      alert(d.detail || 'Update failed');
    }
  };

  const deleteGate = async (s) => {
    if (!window.confirm(`Delete gate user ${s.name}? This is a soft-delete.`)) return;
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/admin/staff/${s.id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (r.ok) fetchStaff();
    else { const d = await r.json(); alert(d.detail || 'Delete failed'); }
  };

  if (loading) return <div className="text-center py-8">Loading gate users...</div>;

  return (
    <div data-testid="gate-master-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <ShieldCheck className="text-emerald-600" size={24} />
          <h1 className="text-2xl font-bold text-gray-900">Gate Master ({allStaff.length})</h1>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          data-testid="add-gate-btn"
        >
          <Plus size={18} /><span>Add Gate Staff</span>
        </button>
      </div>

      <div className="mb-4 text-xs text-gray-600 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
        Gate staff approve <strong>IN/OUT movements</strong> at the entrance via the <em>Gate</em> login.
        Initial password = mobile number (10 digits). They can change it on first login.
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl p-6 shadow-sm border mb-6">
          <h3 className="font-semibold mb-4">Add New Gate Staff</h3>
          <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input data-testid="gate-name-input" type="text" placeholder="Name" value={newGate.name} onChange={(e) => setNewGate({ ...newGate, name: e.target.value })} className="px-3 py-2 border rounded-lg" required />
            <input data-testid="gate-mobile-input" type="tel" placeholder="Mobile (10 digits)" value={newGate.mobile} onChange={(e) => setNewGate({ ...newGate, mobile: e.target.value })} className="px-3 py-2 border rounded-lg" required />
            <input data-testid="gate-staffid-input" type="text" placeholder="Staff ID" value={newGate.staff_id} onChange={(e) => setNewGate({ ...newGate, staff_id: e.target.value })} className="px-3 py-2 border rounded-lg" required />
            <div className="md:col-span-3 flex space-x-2">
              <button data-testid="gate-submit-btn" type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-lg">Add (password = mobile)</button>
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 bg-gray-200 rounded-lg">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <FilterBar
        testId="gate-filters"
        placeholder="Search by name, mobile or staff ID…"
        search={search}
        onSearch={setSearch}
        chips={[
          { key: 'status', label: 'Status', value: statusFilter, options: [
            { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }
          ]}
        ]}
        onChipChange={(_, v) => setStatusFilter(v)}
        onClear={() => { setSearch(''); setStatusFilter('all'); }}
        resultCount={filtered.length}
      />

      <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="gate-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(s => {
                const isEditing = editing === s.id;
                return (
                  <tr key={s.id} className="hover:bg-gray-50" data-testid={`gate-row-${s.id}`}>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="px-2 py-1 border rounded w-full" />
                      ) : <span className="font-medium">{s.name}</span>}
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {isEditing ? (
                        <input value={editForm.staff_id} onChange={(e) => setEditForm({ ...editForm, staff_id: e.target.value })} className="px-2 py-1 border rounded w-full" />
                      ) : (s.staff_id || '-')}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{s.mobile}</td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className="px-2 py-1 border rounded">
                          <option value="active">active</option>
                          <option value="inactive">inactive</option>
                        </select>
                      ) : (
                        <span className={`px-2 py-0.5 text-xs rounded-full ${s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>{s.status}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button onClick={() => saveEdit(s.id)} className="text-green-600 hover:text-green-700 p-1 mx-0.5" data-testid={`save-gate-${s.id}`} title="Save"><Save size={16} /></button>
                          <button onClick={() => setEditing(null)} className="text-gray-600 hover:text-gray-700 p-1 mx-0.5" title="Cancel"><X size={16} /></button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(s)} className="text-blue-600 hover:text-blue-700 p-1 mx-0.5" data-testid={`edit-gate-${s.id}`} title="Edit"><Edit2 size={16} /></button>
                          <button onClick={() => deleteGate(s)} className="text-red-600 hover:text-red-700 p-1 mx-0.5" data-testid={`delete-gate-${s.id}`} title="Delete"><Trash2 size={16} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">{allStaff.length === 0 ? 'No gate staff yet — click "Add Gate Staff" to create one' : 'No gate staff match the current filter'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
